<template>
  <NuxtLink :to="url?.path" class="box bg-white md:flex group/event">
    <div class="shrink-0 flex-1 md:w-1/2 md:max-w-[385px]">
      <div class="aspect-square">
        <MediaImage v-if="image" v-bind="image" :image-style="imageStyle" />
      </div>
    </div>
    <div class="flex-1">
      <div class="box-padding flex flex-col h-full">
        <h3 class="text-2xl">{{ title }}</h3>

        <table v-if="features.length" class="table w-full mt-30">
          <tbody>
            <tr v-for="feature in features" :key="feature.label">
              <th class="align-top pr-20 font-semibold text-left">
                {{ feature.label }}
              </th>
              <td
                :class="{ 'whitespace-pre': feature.type === 'location' }"
                v-html="feature.value"
              />
            </tr>
          </tbody>
        </table>

        <p class="text-base my-30">{{ lead }}</p>

        <div
          class="flex gap-10 mt-auto md:-mb-3 lg:-mb-5 justify-between items-end"
        >
          <ButtonStyle v-slot="{ classes }">
            <div
              class="inline-flex ml-auto mobile-only:hidden opacity-0 transition translate-y-5 group-hover/event:translate-y-0 group-hover/event:opacity-100 whitespace-nowrap button is-primary"
              :class="classes"
            >
              {{ $texts('event.showEvent') }}
            </div>
          </ButtonStyle>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<script lang="ts" setup>
import type { NodeEventTeaserFragment } from '#graphql-operations'
import { falsy } from '#shared/helpers/type'
import { formatEventDate } from '~/helpers/formatEventDate'

const props = defineProps<{
  title?: string
  url?: NodeEventTeaserFragment['url']
  lead?: NodeEventTeaserFragment['lead']
  image?: NodeEventTeaserFragment['image']
  eventDate?: NodeEventTeaserFragment['eventDate']
  location?: NodeEventTeaserFragment['location']
  organiser?: NodeEventTeaserFragment['organiser']
}>()

export type EventFeature = {
  label: string
  value: string | number
  type?: string
}

const { $texts } = useNuxtApp()

const features = computed<EventFeature[]>(() => {
  const features: (EventFeature | undefined | null)[] = []

  if (props.eventDate) {
    features.push({
      label: $texts('event.Date'),
      value: formatEventDate(props.eventDate),
    })
  }

  if (props.location) {
    features.push({
      label: $texts('event.location'),
      value: props.location,
      type: 'location',
    })
  }

  if (props.organiser) {
    features.push({
      label: $texts('event.organizer'),
      value: props.organiser,
    })
  }

  return features.filter(falsy)
})

const imageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  sizes: {
    sm: 767,
    md: 364,
    lg: 385,
  },
})
</script>
